import { normalizeText } from '../../utils/text';

/**
 * @typedef {object} NestedCommune
 * @property {number} id
 * @property {string} name
 * @property {string} [system_name]
 * @property {string|null} [settlement]
 * @property {string|null} [postal_code]
 */

/**
 * @typedef {object} Region
 * @property {number} id
 * @property {string} name
 * @property {string} [system_name]
 * @property {Array<NestedCommune>} communes
 */

/**
 * Value persisted for a region or commune when an address is saved.
 *
 * The backend keys addresses by system name; `name` is only a display fallback
 * for entries that predate it.
 *
 * @param {Region|NestedCommune} place
 * @returns {string}
 */
export const getPlaceValue = (place) => place?.system_name || place?.name || '';

/**
 * Collapses a place name to a comparable key.
 *
 * The catalog sends two fields for every place and stored addresses use either
 * one: an order can hold `"La Florida"` (the `name`) where the options are keyed
 * by `"la_florida"` (the `system_name`). Ignoring case, accents and separators
 * makes the two forms of the same backend record compare equal.
 *
 * @param {string} text
 * @returns {string}
 */
const placeKey = (text) => normalizeText(text).replace(/[\s_-]+/g, '');

/**
 * Resolves a stored region or commune to the value its option carries.
 *
 * Both halves of the comparison come from the catalog the backend serves —
 * `value` is the place's `system_name` and `label` its `name` — so this matches
 * an address against the full backend record instead of only one of its fields.
 *
 * Returns the stored value untouched when nothing matches, so a place that
 * genuinely is not in the catalog is shown rather than silently dropped.
 *
 * @param {Array<{value: string, label: string}>} options
 * @param {string} stored
 * @returns {string}
 */
export const resolvePlaceValue = (options = [], stored) => {
  if (!stored) return '';

  const key = placeKey(stored);
  if (!key) return stored;

  const match = options.find(
    (option) => placeKey(option.value) === key || placeKey(option.label) === key
  );

  return match ? match.value : stored;
};

/**
 * @param {Array<Region>} regions
 * @param {string} regionValue
 * @returns {Region|undefined}
 */
const findRegion = (regions, regionValue) =>
  regions.find((region) => getPlaceValue(region) === regionValue);

/** @param {string} a @param {string} b @returns {number} */
const byLabel = (a, b) => a.label.localeCompare(b.label, 'es');

/**
 * Maps regions to select options.
 *
 * @param {Array<Region>} regions
 * @returns {Array<{value: string, label: string}>}
 */
export const buildRegionOptions = (regions = []) =>
  regions.map((region) => ({
    value: getPlaceValue(region),
    label: region.name,
  }));

/**
 * Maps the communes of the selected region to select options.
 *
 * The catalog carries one row per settlement, so a commune shows up once for
 * every settlement it contains — Ensenada alone accounts for hundreds of rows.
 * The commune is the unit being picked here, so the rows are collapsed onto it.
 *
 * @param {Array<Region>} regions
 * @param {string} regionValue - Value produced by {@link getPlaceValue}
 * @returns {Array<{value: string, label: string}>}
 */
export const buildCommuneOptions = (regions = [], regionValue) => {
  if (!regionValue) return [];

  const region = findRegion(regions, regionValue);
  const seen = new Set();

  return (region?.communes || [])
    .reduce((options, commune) => {
      const value = getPlaceValue(commune);
      if (!value || seen.has(value)) return options;

      seen.add(value);
      options.push({ value, label: commune.name });
      return options;
    }, [])
    .sort(byLabel);
};

/**
 * Maps the settlements of the selected commune to select options.
 *
 * The postal code rides in the label so the list can be searched by it, which
 * is how a Mexican address is usually recalled. The option value is the row id
 * rather than the settlement name, because the same name can appear under more
 * than one postal code.
 *
 * @param {Array<Region>} regions
 * @param {string} regionValue
 * @param {string} communeValue
 * @returns {Array<{value: string, label: string}>}
 */
export const buildSettlementOptions = (regions = [], regionValue, communeValue) => {
  if (!regionValue || !communeValue) return [];

  const region = findRegion(regions, regionValue);
  const seen = new Set();

  return (region?.communes || [])
    .reduce((options, commune) => {
      if (getPlaceValue(commune) !== communeValue || !commune.settlement) return options;

      // Rows repeating the same settlement under the same postal code would
      // read as identical entries, so only the first one is offered.
      const key = `${commune.settlement}|${commune.postal_code || ''}`;
      if (seen.has(key)) return options;

      seen.add(key);
      options.push({
        value: String(commune.id),
        label: commune.postal_code
          ? `${commune.settlement} · ${commune.postal_code}`
          : commune.settlement,
      });
      return options;
    }, [])
    .sort(byLabel);
};

/**
 * Finds a catalog row by its id, so callers can read the settlement and postal
 * code behind a chosen option instead of parsing them back out of its label.
 *
 * @param {Array<Region>} regions
 * @param {string|number} communeId
 * @returns {NestedCommune|undefined}
 */
export const findCommuneById = (regions = [], communeId) => {
  if (!communeId) return undefined;

  const target = String(communeId);

  for (const region of regions) {
    const found = (region.communes || []).find((commune) => String(commune.id) === target);
    if (found) return found;
  }

  return undefined;
};
